import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Alert,
  LinearProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useMe } from '../api/hooks.js';
import { useActiveProject } from '../lib/activeProject.js';
import { api } from '../api/client.js';
import { formatDateTime } from '../lib/format.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

interface Sourcemap {
  id: string;
  releaseVersion: string;
  /** "sourcemap" (JS) or "proguard" (Android mapping.txt). */
  kind?: string;
  fileName: string;
  sizeBytes: number;
  createdAt: string;
}

const fmtSize = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${Math.round(b / 1024)} KB` : `${(b / 1048576).toFixed(1)} MB`);

/** Upload & manage JS sourcemaps so error stacks resolve to original source. */
export function SourcemapsPage() {
  const all = useT();
  const t = all.keys.sourcemaps;
  const common = all.common;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { projectId: activeProject } = useActiveProject(orgId);

  const [release, setRelease] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  useEffect(() => setMsg(null), [activeProject]);

  const base = `/v1/orgs/${orgId}/projects/${activeProject}/sourcemaps`;
  const list = useQuery({
    enabled: !!orgId && !!activeProject,
    queryKey: ['sourcemaps', orgId, activeProject],
    queryFn: async () => (await api.get(base)).data.data as Sourcemap[],
  });

  const sort = useSort<'file' | 'release' | 'size' | 'uploaded'>('uploaded');
  const rows = sort.sorted(list.data ?? [], {
    file: (s) => s.fileName,
    release: (s) => s.releaseVersion,
    size: (s) => s.sizeBytes,
    uploaded: (s) => s.createdAt,
  });

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    if (!release.trim()) {
      setMsg({ kind: 'error', text: 'Enter a release version first (it must match your app’s reported release).' });
      return;
    }
    setBusy(true);
    setMsg(null);
    let ok = 0;
    try {
      for (const file of Array.from(files)) {
        const map = await file.text();
        const fileName = file.name.replace(/\.map$/i, ''); // main.abc.js.map → main.abc.js
        // An Android build's symbol file is always mapping.txt; anything else
        // is a JS sourcemap. Detecting it beats making the user pick.
        const kind = /^mapping.*\.txt$/i.test(file.name) ? 'proguard' : 'sourcemap';
        await api.post(base, { releaseVersion: release.trim(), fileName, map, kind });
        ok++;
      }
      setMsg({ kind: 'success', text: `Uploaded ${ok} symbol file${ok === 1 ? '' : 's'} for release ${release.trim()}.` });
      qc.invalidateQueries({ queryKey: ['sourcemaps', orgId, activeProject] });
    } catch (e) {
      setMsg({ kind: 'error', text: t.uploadFailed((e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? String(e)) });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (id: string) => {
    await api.delete(`${base}/${id}`);
    qc.invalidateQueries({ queryKey: ['sourcemaps', orgId, activeProject] });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5">{t.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t.subtitle}
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label={t.release}
            placeholder={t.releasePlaceholder}
            value={release}
            onChange={(e) => setRelease(e.target.value)}
            sx={{ flex: 1 }}
            helperText={t.releaseHelp}
          />
          <input ref={fileRef} type="file" accept=".map,.json,.txt" multiple hidden onChange={(e) => onFiles(e.target.files)} />
          <Button variant="contained" startIcon={<UploadFileIcon />} disabled={busy} onClick={() => fileRef.current?.click()}>
            {t.upload}
          </Button>
        </Stack>
        {busy && <LinearProgress sx={{ mt: 1.5 }} />}
        {msg && <Alert severity={msg.kind} sx={{ mt: 1.5 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        {list.isLoading && <LinearProgress />}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {sort.head('file', t.columns.file)}
                {sort.head('release', t.columns.release)}
                {sort.head('size', t.columns.size, 'right')}
                {sort.head('uploaded', t.columns.uploaded)}
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {s.kind === 'proguard'
                        ? <PhoneAndroidIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        : <DataObjectIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                      <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>{s.fileName}</Typography>
                      {s.kind === 'proguard' && (
                        <Chip size="small" variant="outlined" label={t.android} sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{s.releaseVersion}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" color="text.secondary">{fmtSize(s.sizeBytes)}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{formatDateTime(s.createdAt)}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title={common.delete}>
                      <IconButton size="small" onClick={() => remove(s.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {list.data && list.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                      {t.empty}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
